export type Maybe<T> = T | null

type SecurityRole = 'user' | 'support' | 'restaurant' | 'restaurant_admin' | 'restaurant_staff'
type Seconds = number
type WTimeRef = 'fire_time' | 'order_time'
type WTransitionType = 'scheduled_business_date' | 'scheduled_date' | 'scheduled' | 'timer' | 'manual'

export type WVariableKind = 'time' | 'count' | 'percent' | 'string'
export type WVariableFormat = 'url' | 'template' | 'text' | 'email'

/**
 * Self-contained variable lookup emitted at usage sites in the workflow content
 * when a script references `@variable`. Mirrors Chef's model — see
 * zuppler-chef docs/ow-variables.md. The runtime resolves it to the override
 * value or falls back to `default`.
 */
export interface WVariableRef {
  variable: string
  kind: WVariableKind
  format?: WVariableFormat
  default: number | string
}

/** Registry entry in Workflow.variables — every variable the workflow defines. */
export interface WVariable {
  name: string
  kind: WVariableKind
  format?: WVariableFormat
  default: number | string
}

/**
 * Restaurant's current override for a workflow variable, as returned by
 * `restaurant { workflow_variables { name value } }`. value: integer for
 * time/count/percent, string for string-kind; null means revert to default.
 */
export interface WorkflowVariableOverride {
  name: string
  value: number | string | null
}

/** name -> current override value (rows with null values are skipped) */
export type VariableOverrides = { [name: string]: number | string }

/** True when a content value is a variable lookup rather than a plain scalar. */
export const isVariableRef = (v: any): v is WVariableRef =>
  !!v && typeof v === 'object' && typeof (v as WVariableRef).variable === 'string'

/** Current overrides as a lookup map — nulls skipped (they mean "use default"). */
export const overridesByName = (rows: Maybe<WorkflowVariableOverride[]>): VariableOverrides =>
  (rows || []).reduce((map, row) => {
    if (row && row.value !== null && row.value !== undefined) map[row.name] = row.value
    return map
  }, {} as VariableOverrides)

export interface WorkflowInfo {
  id: Maybe<number>
  uuid: string
  name: string
  description: string
  author: string
  url: string
  created_at: string
}
export interface WorkflowModel extends WorkflowInfo {
  content: Maybe<Workflow>
  script: string
}

export interface Workflow {
  nodes: WNode[]
  transitions: WTransition[]
  /** present only when the workflow script defines variables */
  variables?: WVariable[]
}

export interface WNode {
  id: string
  name: string
  description: Maybe<string>
  actions: WNodeAction[]
}
export interface WNodeAction {
  name: string
  notes: string
  success: Maybe<string>
  failure: Maybe<string>
  accept: Maybe<string>
  reject: Maybe<string>
  params: {
    event?: string
    verb?: string
    state?: string
    template?: string | WVariableRef
    // NotifyChannel custom endpoint
    url?: Maybe<string | WVariableRef>
    // SubscribeRestaurant for <duration>
    timeout?: number | WVariableRef
    // WaitForFeedback happy threshold
    threshold?: number | WVariableRef
    // CustomEmail recipient
    address?: string | WVariableRef
    // OutboundCall prompt
    prompt?: string | WVariableRef
  }
}

export interface WTransition {
  from_node: string
  to_node: string
  transition_type: WTransitionType
}

export interface WTimeoutTransition extends WTransition {
  time: WTime
  limits: Maybe<number | WVariableRef> // Max enters in the same state
}
export type WManualTransitionCategory = 'primary' | 'secondary' | 'danger' | 'info' | 'warn'

export interface WManualTransition extends WTransition {
  name: string
  label: string | null
  category: WManualTransitionCategory | null
  roles: SecurityRole[]
}

export interface WScheduledTransition extends WTransition {
  time: WTimeFunction
}

export interface WScheduledDateTransition extends WTransition {
  time: WDateTimeFunction
}

export type WScheduledBusinessDateTransition = WScheduledDateTransition

export interface WTime {
  time_offset: Seconds | WVariableRef
}
export interface WTimeFunction extends WTime {
  ref: WTimeRef
}

export interface WDateTimeFunction extends WTimeFunction {
  day_offset: number
}
